import { useState } from "react"
import cc from "classcat"
import { motion } from "framer-motion"

import { Disclosure } from "@headlessui/react"
import { ChevronUpIcon } from "@heroicons/react/20/solid"
import Divided from "components/organisms/common/Divided"

const variants = {
  open: {
    y: 0,
    opacity: 1,
    transition: {
      y: { velocity: -100 },
    },
  },
  close: {
    y: 50,
    opacity: 0,
    transition: {
      y: { velocity: 100 },
    },
  },
}
const DisclosuredPanel = ({
  index,
  open,
  onClick,
  theme,
  buttonStyle,
  ...item
}) => {
  const styles = {
    dark: {
      button: "bg-zinc-800 hover:bg-zinc-800/90 text-zinc-100",
      text: "text-zinc-200",
      chevron: "text-zinc-100/30",
    },
    darkyellow: {
      button: "bg-kkm-yellow/90 hover:bg-kkm-yellow/100 text-zinc-900",
      text: "text-zinc-200",
      chevron: "text-zinc-100/30",
    },
    light: {
      button: "bg-kkm-green/90 hover:bg-kkm-green/100 text-zinc-900",
      text: " text-zinc-900",
      chevron: "text-zinc-900/30",
    },
    lightyellow: {
      button: "bg-kkm-yellow/90 hover:bg-kkm-yellow/100 text-zinc-900",
      text: " text-zinc-900",
      chevron: "text-zinc-900/30",
    },
    green: {
      button: "bg-kkm-lightgreen/90 hover:bg-kkm-lightgreen/100  text-zinc-900",
      text: " text-zinc-900",
      chevron: "text-zinc-900/30",
    },
    red: {
      button: "bg-kkm-red/80 hover:bg-kkm-red/100  text-zinc-900",
      text: " text-zinc-900",
      chevron: "text-zinc-900/30",
    },
  }

  return (
    <Disclosure
      defaultOpen={index === 0}
      onClick={() => item.description && onClick(index)}
    >
      <div>
        <Disclosure.Button
          className={cc([
            "leading-0 -mr-4 flex w-full items-center justify-center rounded-3xl py-4 text-left font-display text-base font-medium transition-all focus:outline-none focus-visible:ring focus-visible:ring-teal-500 focus-visible:ring-opacity-75",
            styles[buttonStyle]?.button,
            !item.description && "cursor-default",
            item.icon ? "px-5" : "pl-8 pr-6",
          ])}
        >
          <div className="w-full">
            <div className="relative flex items-center">
              {item.icon ? (
                <item.icon className="-my-3 mr-3 h-8 w-8 shrink-0 text-white " />
              ) : null}
              <div className="flex grow items-center justify-between">
                <div className="mt-[0.15em] inline grow text-xl leading-6">
                  {item.name}
                </div>
                {item.description && (
                  <ChevronUpIcon
                    className={cc([
                      "mx-1 mb-1 mt-0.5 inline-block h-6 w-6 shrink-0",
                      styles[buttonStyle]?.chevron,
                      open ? "" : "rotate-180 transform",
                    ])}
                  />
                )}
              </div>
            </div>
            <motion.div
              layout
              variants={variants}
              className={cc([
                "overflow-hidden font-body text-base",
                styles[buttonStyle]?.text,
                open ? "pb-2 pt-4" : "max-h-0",
                item.icon ? "" : "pt-0",
              ])}
              animate={open ? "open" : "close"}
            >
              {item.description}
            </motion.div>
          </div>
        </Disclosure.Button>
      </div>
    </Disclosure>
  )
}

export const Disclosured = ({
  list,
  initialActive = null,
  theme,
  buttonStyle,
}) => {
  const [activeCard, setActiveCard] = useState(initialActive)

  const handlePanelClick = (index) => {
    if (index !== activeCard) {
      setActiveCard(index)
    } else {
      setActiveCard(null)
    }
  }
  return (
    <div className="space-y-3 md:-mr-4">
      {list.map((item, index) => (
        <DisclosuredPanel
          {...item}
          index={index}
          open={activeCard === index}
          onClick={handlePanelClick}
          theme={theme}
          buttonStyle={buttonStyle || theme}
        />
      ))}
    </div>
  )
}

type DisclosureListProps = {
  title: string
  list: Array<{ name: string; description: React.ReactNode; icon?: any }>
  Image: (props?: any) => JSX.Element
  initialActive?: number
  theme?: "green" | "darkgreen" | "yellow" | "light" | "dark"
  reversed?: boolean
  fullHeight?: boolean
  contentClassName?: string
  buttonStyle?: string
}

export default function DisclosureList({
  title,
  list,
  Image,
  initialActive = null,
  theme = "green",
  reversed = false,
  fullHeight = false,
  contentClassName = "",
  buttonStyle = "",
}: DisclosureListProps) {
  return (
    <Divided
      Image={Image}
      title={title}
      reversed={reversed}
      theme={theme}
      listLength={list.length}
      fullHeight={fullHeight}
      contentClassName={contentClassName}
    >
      <Disclosured
        list={list}
        initialActive={initialActive}
        theme={theme}
        buttonStyle={buttonStyle}
      />
    </Divided>
  )
}
